/**
 * English copy catalog for the Books & Rules / campaign feature
 * (`@gamecodex/world-ui` → `features/books`).
 *
 * Keys mirror the `t('books.*')` calls in the feature's React components. Values
 * are the exact original English strings extracted during the i18n sweep.
 */
export const booksEn = {
  // BackerDownloadModal.tsx
  'books.backerModal.title': 'Claim Your Backer PDF',
  'books.backerModal.sendLinkError': 'We could not send your sign-in link right now. Try again shortly.',
  'books.backerModal.checkInboxPrefix': 'Check your inbox at',
  'books.backerModal.checkInboxSuffix':
    'If you backed with that email, a sign-in link is on its way — click it to claim your copy.',
  'books.backerModal.linkAutoSignIn': 'The link opens on this site and signs you in automatically. No password needed.',
  'books.backerModal.signInIntro':
    "Enter the email you backed with. We'll send a one-click sign-in link, then prepare your personal copy.",
  'books.backerModal.pledgeEmailLabel': 'Pledge account email',
  'books.backerModal.emailLinkCta': 'Email me a sign-in link',
  'books.backerModal.prepareTimeout':
    'Your copy is taking longer than expected to prepare. Please try again in a few minutes.',
  'books.backerModal.notEntitled': "You're signed in, but your pledge doesn't include this book.",
  'books.backerModal.notClaimable': 'This book is not offered as a backer download.',
  'books.backerModal.prepareError': 'We could not prepare your download right now. Try again shortly.',
  'books.backerModal.signedInAs': 'Signed in as',
  'books.backerModal.signOut': 'Sign out',
  'books.backerModal.verified': 'Verified',
  'books.backerModal.welcomeBack': ' — welcome back,',
  'books.backerModal.backerNumber': '(Backer #{number})',
  'books.backerModal.copyReady':
    "Your personal copy is ready. This link is personal and expires shortly — please don't share it.",
  'books.backerModal.downloadPdf': 'Download your PDF',
  'books.backerModal.dtrpgEmailLabel': 'DriveThruRPG account email',
  'books.backerModal.optional': '(optional)',
  'books.backerModal.dtrpgWarn':
    'Add this to also mirror the purchase to your DriveThruRPG library. Skip it and your copy is still downloadable here.',
  'books.backerModal.preparingCopy': 'Preparing your copy…',
  'books.backerModal.prepareDownload': 'Prepare & Download',

  // BackerVerifyPage.tsx
  'books.backerVerify.missingToken': 'This sign-in link is missing its token. Request a new link from the book page.',
  'books.backerVerify.invalidToken':
    'This sign-in link is invalid or has expired. Request a new one from the book page.',
  'books.backerVerify.seoTitle': 'Backer sign-in',
  'books.backerVerify.seoDescription': 'Verifying your Grymwyrd backer sign-in link.',
  'books.backerVerify.confirming': 'Confirming your backer sign-in…',
  'books.backerVerify.signedInHeading': "You're signed in",
  'books.backerVerify.signedInAs': 'Signed in as',
  'books.backerVerify.headBackPrefix': 'Head back to the book and click',
  'books.backerVerify.downloadWord': 'Download',
  'books.backerVerify.headBackSuffix': 'to claim your copy.',
  'books.backerVerify.backToBook': 'Back to your book',
  'books.backerVerify.browseAthenaeum': 'Browse the Athenaeum',
  'books.backerVerify.problemHeading': 'Sign-in link problem',

  // BookCard.tsx
  'books.card.viewAria': 'View {title}',
  'books.card.coverAlt': 'Cover of {title}',
  'books.card.owned': 'Owned',

  // BookCover.tsx
  'books.cover.coverAlt': 'Cover of {title}',

  // BookStorePage.tsx
  'books.store.seoTitle': 'Books & Rules',
  'books.store.seoDescription':
    'Tomes of the eternal twilight — core rulebooks, the illustrated SRD, and grimdark adventures. Buy to unlock modules and earn coins.',
  'books.store.eyebrow': 'The Athenaeum',
  'books.store.heading': 'Books & Rules',
  'books.store.intro':
    'Acquire the tomes that bind the world together. Every purchase unlocks playable modules in your campaigns and rewards you with coins for the in-world economy.',
  'books.store.filterAria': 'Filter books by type',
  'books.store.filterAll': 'All',
  'books.store.loading': 'Unsealing the archive…',
  'books.store.empty': 'No tomes match this category yet. The scribes are still at work.',

  // BuyButton.tsx
  'books.buy.checkoutUnavailable': 'Checkout is not available yet. Please try again soon.',
  'books.buy.signInToClaim': 'Sign in to claim this tome.',
  'books.buy.ledgerSealed': 'The merchant’s ledger is sealed for now — checkout is coming soon.',
  'books.buy.inLibrary': 'In Your Library',
  'books.buy.openingVault': 'Opening the vault…',
  'books.buy.claimFree': 'Claim Free',
  'books.buy.buyPrice': 'Buy — {price}',
  'books.buy.unlockPrefix': 'Buy → unlock modules +',
  'books.buy.coinsSuffix': '{coins} coins',

  // BookDetailPage.tsx
  'books.detail.loading': 'Turning the pages…',
  'books.detail.backToAthenaeum': 'Back to the Athenaeum',
  'books.detail.unlockOwned': 'You own this tome — its Grymwyrd lore is unlocked across the encyclopedia.',
  'books.detail.unlockClaim': 'Claim this tome to unlock its Grymwyrd lore across the encyclopedia.',
  'books.detail.returnTo': 'Return to {label}',
  'books.detail.backerCta': 'Backer? Download your PDF',
  'books.detail.coinsEarned': 'You earned {coins} coins from this purchase.',
  'books.detail.coinsGranted': 'Purchase grants {coins} coins to your hoard.',
  'books.detail.byAuthor': 'By {author}',
  'books.detail.statPrice': 'Price',
  'books.detail.statPages': 'Pages (last render)',
  'books.detail.statWords': 'Words (last render)',
  'books.detail.ofThisTome': 'Of This Tome',
  'books.detail.noDescription': 'No description has been inscribed for this volume yet.',
  'books.detail.previewHeading': 'Preview',
  'books.detail.previewBlurb': 'Glimpse a sample of this tome before you claim it.',
  'books.detail.readPreview': 'Read a preview',
  'books.detail.tocHeading': 'Table of Contents',
  'books.detail.tocEmpty': 'The contents of this tome have not yet been catalogued.',
  'books.detail.pageAbbrev': 'p. {page}',
  'books.detail.unlockedHeading': 'Unlocked Encyclopedia Entries',
  'books.detail.unlockedOwnedNote': 'Now part of your living encyclopedia:',
  'books.detail.unlockedLockedNote':
    'Purchase this tome to inscribe its creatures, spells, and secrets into your encyclopedia — and claim',
  'books.detail.coinsAmount': '{coins} coins',
  'books.detail.attributionsHeading': 'Attributions & Licensing',
  'books.detail.attributionSource': 'Source',
  'books.detail.attributionLink': 'link',

  // CampaignLandingPage.tsx
  'books.campaign.liveCampaign': 'Live Campaign',
  'books.campaign.backThisProject': 'Back This Project',
  'books.campaign.viewStretchGoals': 'View Stretch Goals',
  'books.campaign.pledged': 'Pledged',
  'books.campaign.backers': 'Backers',
  'books.campaign.pitchHeading': 'The Pitch',
  'books.campaign.backerUnlockedHeading': 'Backer-Unlocked Entries',
  'books.campaign.backerUnlockedBlurb':
    'Backers get early access to content that flows straight into the living encyclopedia. Here is a taste of what is already canon:',
  'books.campaign.previewsEmpty': 'New canon is being inscribed — check back as goals unlock.',
  'books.campaign.choosePledge': 'Choose Your Pledge',
  'books.campaign.mostBacked': 'Most Backed',
  'books.campaign.pledgeAmount': 'Pledge {amount}',
  'books.campaign.pledgeFooter':
    'Pledges are processed securely at checkout. Backing rewards (coins, modules, and encyclopedia unlocks) are granted to your Grymwyrd account once the campaign service goes live.',

  // CampaignLandingPage.tsx — fallback campaign identity, shown only when no real campaign row exists yet
  'books.campaign.fallbackTitle': 'Grymwyrd Cataclysm',
  'books.campaign.fallbackTagline': 'A grimdark horror-fantasy RPG where your legend becomes canon.',
  'books.campaign.fallbackPitch':
    'The veil is thinning. Grymwyrd Cataclysm is a fully-realized tabletop world — a living encyclopedia of monsters, spells, and cursed places that grows with every backer. Fund the campaign and your support literally writes the world: each stretch goal auto-populates the shared encyclopedia with new playable canon you can drop straight into your campaigns.',

  // LanguageSelect.tsx
  'books.language.label': 'Language',

  // PreviewModal.tsx
  'books.preview.title': 'Preview — {title}',
  'books.preview.pdfAria': 'Preview PDF of {title}',
  'books.preview.cannotDisplay': 'Your conjurer cannot display the preview inline.',
  'books.preview.openNewTab': 'Open the preview in a new tab',
  'books.preview.preparingHeading': 'The preview is being prepared.',
  'books.preview.preparingBody':
    'A sample of this tome is being inscribed by the scriptorium and will appear here shortly. In the meantime, glimpse what lies within:',
  'books.preview.excerptLabel': 'Excerpt — {title}',
  'books.preview.excerptFallback':
    'Beyond this threshold lie pages steeped in dread and wonder. Acquire the tome to read on.',
  'books.preview.noExcerpt': 'No excerpt has been catalogued for this volume yet.',

  // RulesPage.tsx
  'books.rules.sectionSpells': 'Spells',
  'books.rules.sectionMonsters': 'Monsters',
  'books.rules.sectionMagicItems': 'Magic Items',
  'books.rules.sectionWeapons': 'Weapons',
  'books.rules.sectionArmor': 'Armor',
  'books.rules.sectionSkills': 'Skills',
  'books.rules.entriesCount': '{entries} entries',
  'books.rules.readingLabel': 'Reading {label}…',
  'books.rules.noneCatalogued': 'No {label} catalogued in this system yet.',
  'books.rules.browseAll': 'Browse all {label} in the encyclopedia →',
  'books.rules.seoTitle': 'Rules & SRD',
  'books.rules.seoDescription':
    'Browse the System Reference Document — spells, monsters, items and the rules that bind the eternal twilight. 5e SRD now, with more systems to come.',
  'books.rules.eyebrow': 'The Codex',
  'books.rules.heading': 'Rules & System Reference',
  'books.rules.intro':
    'The open game content that powers Grymwyrd. Browse rules-relevant entries and dive into the full encyclopedia for stat blocks and lore. Grymwyrd ships with the 5e SRD today — PF2e and other systems are coming.',
  'books.rules.loading': 'Consulting the codex…',
  'books.rules.gameSystems': 'Game Systems',
  'books.rules.noSystems': 'No game systems are registered yet.',
  'books.rules.openSrd': 'Open SRD',
  'books.rules.lockedComingSoon': 'Locked — coming soon',
  'books.rules.referenceContent': '{name} — Reference Content',
  'books.rules.releasedUnder': 'Released under {license}',
  'books.rules.licenseLink': 'license',

  // StretchGoals.tsx
  'books.stretch.goalProgressAria': '{title} funding progress',
  'books.stretch.unlockedAdded': 'Unlocked — added to the encyclopedia',
  'books.stretch.percentFunded': '{pct}% funded',
  'books.stretch.autoPopulates': 'Auto-populates the encyclopedia',
  'books.stretch.raisedSoFar': 'Raised so far',
  'books.stretch.backers': 'Backers',
  'books.stretch.overallAria': 'Overall campaign funding',
  'books.stretch.heading': 'Stretch Goals',
} as const
